import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Upload,
    FileText,
    TrendingUp,
    Sparkles,
    ArrowUpRight,
    AlertCircle,
    Coins,
    Calendar,
    CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";

const PLAN_CAP = { free: 3, starter: 10, pro: 30, business: 999 };

function getPlanUsage(profile) {
    const plan = profile?.plan || "free";
    const cap = PLAN_CAP[plan] || 3;
    const credits = profile?.credits ?? 0;
    const used = Math.max(0, cap - credits);
    const usagePct = cap === 999 ? 0 : (used / cap) * 100;
    return { plan, cap, credits, used, usagePct };
}

export default function Dashboard() {
    const { profile } = useAuth();
    const [recent, setRecent] = useState([]);
    const [stats, setStats] = useState({ total: 0 });

    const { plan, cap, credits, used, usagePct } = getPlanUsage(profile);

    useEffect(() => {
        if (!profile?.id) return;
        (async () => {
            const { data } = await supabase
                .from("tender_analyses")
                .select("*")
                .order("created_at", { ascending: false })
                .limit(5);
            setRecent(data || []);

            const { count: total } = await supabase
                .from("tender_analyses")
                .select("*", { count: "exact", head: true });
            setStats({ total: total || 0 });
        })();
    }, [profile?.id]);

    const firstName = (profile?.full_name || "there").split(" ")[0];

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                        Good morning, {firstName} 👋
                    </h1>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        Here&apos;s what&apos;s happening with your tenders today.
                    </p>
                </div>
                <Link to="/dashboard/upload">
                    <Button
                        size="lg"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/25"
                        data-testid="dashboard-quick-upload-btn"
                    >
                        <Upload className="h-4 w-4 mr-2" /> Analyze New Tender
                    </Button>
                </Link>
            </div>

            <StatsGrid
                plan={plan}
                cap={cap}
                credits={credits}
                used={used}
                usagePct={usagePct}
                total={stats.total}
            />

            <div className="grid lg:grid-cols-3 gap-6">
                <RecentAnalyses recent={recent} />
                <div className="space-y-4">
                    <UpgradeCard />
                    <AccountCard profile={profile} />
                </div>
            </div>
        </div>
    );
}

function StatsGrid({ plan, cap, credits, used, usagePct, total }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                label="Current Plan"
                value={plan === "free" ? "Free" : plan}
                sub={plan === "free" ? "Default starter plan" : "Renews monthly"}
                icon={Sparkles}
                color="indigo"
            />
            <StatCard
                label="Credits Remaining"
                value={credits}
                valueSub={cap === 999 ? "/ ∞" : `/ ${cap}`}
                sub={
                    <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
                            style={{ width: `${usagePct}%` }}
                        />
                    </div>
                }
                icon={Coins}
                color="emerald"
            />
            <StatCard
                label="Credits Used"
                value={used}
                sub={
                    <p className="text-xs text-slate-500 mt-2">Across all analyses</p>
                }
                icon={TrendingUp}
                color="violet"
            />
            <StatCard
                label="Total Analyses"
                value={total}
                sub={<p className="text-xs text-slate-500 mt-2">All-time count</p>}
                icon={CheckCircle2}
                color="amber"
            />
        </div>
    );
}

function RecentAnalyses({ recent }) {
    return (
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
                <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                        Recent Analyses
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Your most recent tender analyses
                    </p>
                </div>
                <Link
                    to="/dashboard/history"
                    className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1"
                >
                    View all <ArrowUpRight className="h-3 w-3" />
                </Link>
            </div>
            {recent.length === 0 ? <EmptyRecent /> : <RecentList recent={recent} />}
        </div>
    );
}

function EmptyRecent() {
    return (
        <div className="p-10 text-center">
            <FileText className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                No analyses yet
            </p>
            <p className="text-xs text-slate-500 mt-1">
                Upload your first tender PDF to see it here.
            </p>
            <Link to="/dashboard/upload" className="mt-5 inline-block">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Tender
                </Button>
            </Link>
        </div>
    );
}

function RecentList({ recent }) {
    return (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {recent.map((r) => (
                <Link
                    key={r.id}
                    to={`/dashboard/analysis/${r.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-slate-950/50 transition-colors"
                    data-testid={`recent-analysis-${r.id}`}
                >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 shrink-0">
                        <FileText className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-50 truncate">
                            {r.result?.name || r.file_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                            {r.result?.department ? `${r.result.department} • ` : ""}
                            {new Date(r.created_at).toLocaleString()}
                        </p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 capitalize">
                        {r.status}
                    </Badge>
                </Link>
            ))}
        </div>
    );
}

function UpgradeCard() {
    return (
        <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-6 overflow-hidden relative">
            <div className="absolute inset-0 grid-pattern-dark opacity-10" />
            <div className="relative">
                <Sparkles className="h-5 w-5" />
                <h4 className="mt-3 font-bold text-lg">Need more credits?</h4>
                <p className="mt-2 text-sm text-indigo-100 leading-relaxed">
                    Upgrade to Pro or Business and analyze more tenders this month.
                </p>
                <Link to="/dashboard/billing" className="mt-5 inline-block">
                    <Button
                        size="sm"
                        className="bg-white text-indigo-700 hover:bg-slate-100 font-semibold"
                        data-testid="dashboard-upgrade-cta"
                    >
                        Upgrade Now
                    </Button>
                </Link>
            </div>
        </div>
    );
}

function AccountCard({ profile }) {
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-50">
                    Account
                </h4>
            </div>
            <div className="mt-4 space-y-3 text-xs">
                <Row label="Name" value={profile?.full_name || "—"} />
                <Row label="Email" value={profile?.email} />
                <Row
                    label="Plan"
                    value={<span className="capitalize">{profile?.plan || "free"}</span>}
                />
                <Row
                    label="Joined"
                    value={
                        profile?.created_at
                            ? new Date(profile.created_at).toLocaleDateString()
                            : "—"
                    }
                />
            </div>
        </div>
    );
}

function StatCard({ label, value, valueSub, sub, icon: Icon, color }) {
    const colors = {
        indigo: "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600",
        emerald: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600",
        violet: "bg-violet-50 dark:bg-violet-950/40 text-violet-600",
        amber: "bg-amber-50 dark:bg-amber-950/40 text-amber-600",
    };
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex items-start justify-between">
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {label}
                    </p>
                    <p className="mt-2 text-2xl font-bold font-mono text-slate-900 dark:text-slate-50 truncate capitalize">
                        {value}
                        {valueSub && (
                            <span className="text-base text-slate-400 font-normal">
                                {" "}
                                {valueSub}
                            </span>
                        )}
                    </p>
                </div>
                <div
                    className={`flex h-9 w-9 items-center justify-center rounded-xl ${colors[color]}`}
                >
                    <Icon className="h-4 w-4" />
                </div>
            </div>
            {typeof sub === "string" ? (
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {sub}
                </p>
            ) : (
                sub
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="text-slate-500">{label}</span>
            <span className="font-medium text-slate-900 dark:text-slate-100 truncate text-right">
                {value}
            </span>
        </div>
    );
}
